import { businessData } from '../config/businessData'

export type CheckoutResolvedPlace = {
  placeId: string
  formattedAddress: string
  suburb: string
  postcode: string
  latitude: number
  longitude: number
}

type GoogleAddressComponent = {
  longText?: string
  shortText?: string
  types: string[]
}

type GoogleLatLng = {
  lat: () => number
  lng: () => number
}

type GooglePlace = {
  id?: string
  formattedAddress?: string
  addressComponents?: GoogleAddressComponent[]
  location?: GoogleLatLng
  fetchFields: (request: { fields: string[] }) => Promise<void>
}

type GooglePlacePrediction = {
  text: { toString: () => string }
  toPlace: () => GooglePlace
}

type GoogleAutocompleteSuggestion = {
  placePrediction?: GooglePlacePrediction
}

type GoogleAutocompleteRequest = {
  input: string
  includedRegionCodes: string[]
  includedPrimaryTypes: string[]
  language: string
  region: string
  sessionToken: unknown
}

type GooglePlacesLibrary = {
  AutocompleteSessionToken: new () => unknown
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (
      request: GoogleAutocompleteRequest,
    ) => Promise<{ suggestions: GoogleAutocompleteSuggestion[] }>
  }
}

type GoogleMapsRuntime = {
  maps: {
    importLibrary: (library: 'places') => Promise<GooglePlacesLibrary>
  }
}

type GoogleMapsWindow = Window & {
  google?: GoogleMapsRuntime
  __sgCheckoutGoogleMapsReady?: () => void
}

const SUGGESTIONS_ID = 'checkout-address-suggestions'
const MIN_AUTOCOMPLETE_CHARACTERS = 3
const AUTOCOMPLETE_DEBOUNCE_MS = 180
const GOOGLE_CALLBACK = '__sgCheckoutGoogleMapsReady'

let mapsLoader: Promise<GoogleMapsRuntime | null> | null = null
let placesLoader: Promise<GooglePlacesLibrary | null> | null = null
let selectedPlace: CheckoutResolvedPlace | null = null

function runtime(): GoogleMapsRuntime | null {
  return (window as GoogleMapsWindow).google ?? null
}

function loadGoogleMaps(): Promise<GoogleMapsRuntime | null> {
  const existing = runtime()
  if (existing?.maps.importLibrary) return Promise.resolve(existing)
  if (mapsLoader) return mapsLoader

  const key = businessData.delivery.addressAutocomplete.publishableKey
  if (!key) return Promise.resolve(null)

  mapsLoader = new Promise((resolve) => {
    const googleWindow = window as GoogleMapsWindow
    let settled = false
    let timeoutId: number | null = null

    const finish = (value: GoogleMapsRuntime | null) => {
      if (settled) return
      settled = true
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      if (googleWindow.__sgCheckoutGoogleMapsReady === handleReady) {
        delete googleWindow.__sgCheckoutGoogleMapsReady
      }
      resolve(value)
    }

    const handleReady = () => {
      const loaded = runtime()
      finish(loaded?.maps.importLibrary ? loaded : null)
    }

    googleWindow.__sgCheckoutGoogleMapsReady = handleReady

    const staleScript = document.querySelector<HTMLScriptElement>('script[data-sg-google-maps="true"]')
    if (staleScript) staleScript.remove()

    const script = document.createElement('script')
    const params = new URLSearchParams({
      key,
      loading: 'async',
      v: 'weekly',
      libraries: 'places',
      region: 'NZ',
      language: 'en',
      callback: GOOGLE_CALLBACK,
    })
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`
    script.async = true
    script.defer = true
    script.dataset.sgGoogleMaps = 'true'
    script.addEventListener('error', () => finish(null), { once: true })
    document.head.appendChild(script)

    timeoutId = window.setTimeout(() => finish(null), 10000)
  })

  return mapsLoader
}

async function loadPlacesLibrary(): Promise<GooglePlacesLibrary | null> {
  if (placesLoader) return placesLoader

  placesLoader = loadGoogleMaps().then(async (google) => {
    if (!google?.maps.importLibrary) return null
    try {
      return await google.maps.importLibrary('places')
    } catch {
      return null
    }
  })

  return placesLoader
}

function componentValue(components: GoogleAddressComponent[], ...types: string[]): string {
  for (const type of types) {
    const component = components.find((item) => item.types.includes(type))
    if (component?.longText) return component.longText
  }
  return ''
}

function componentShortValue(components: GoogleAddressComponent[], type: string): string {
  const component = components.find((item) => item.types.includes(type))
  return component?.shortText ?? ''
}

function setControlledInputValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  setter?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

function createSuggestionsContainer(addressInput: HTMLInputElement): HTMLDivElement {
  document.getElementById(SUGGESTIONS_ID)?.remove()

  const container = document.createElement('div')
  container.id = SUGGESTIONS_ID
  container.className = 'checkout-v3-address-suggestions'
  container.setAttribute('role', 'listbox')
  container.setAttribute('aria-label', 'Address suggestions')
  container.hidden = true
  document.body.appendChild(container)

  addressInput.setAttribute('aria-autocomplete', 'list')
  addressInput.setAttribute('aria-controls', SUGGESTIONS_ID)
  addressInput.setAttribute('aria-expanded', 'false')

  return container
}

function positionSuggestions(container: HTMLDivElement, addressInput: HTMLInputElement) {
  const rect = addressInput.getBoundingClientRect()
  container.style.top = `${rect.bottom + window.scrollY + 5}px`
  container.style.left = `${rect.left + window.scrollX}px`
  container.style.width = `${rect.width}px`
}

function hideSuggestions(container: HTMLDivElement, addressInput: HTMLInputElement) {
  container.hidden = true
  container.replaceChildren()
  addressInput.setAttribute('aria-expanded', 'false')
  addressInput.removeAttribute('aria-activedescendant')
}

export function clearSelectedCheckoutPlace() {
  selectedPlace = null
}

export function getSelectedCheckoutPlace(address: string): CheckoutResolvedPlace | null {
  if (!selectedPlace) return null
  return selectedPlace.formattedAddress.trim() === address.trim() ? selectedPlace : null
}

export async function attachCheckoutAddressAutocomplete({
  addressInput,
  suburbInput,
  postcodeInput,
}: {
  addressInput: HTMLInputElement
  suburbInput: HTMLInputElement | null
  postcodeInput: HTMLInputElement | null
}): Promise<() => void> {
  const places = await loadPlacesLibrary()
  if (!places) return () => undefined

  const suggestionsContainer = createSuggestionsContainer(addressInput)
  let sessionToken: unknown = new places.AutocompleteSessionToken()
  let debounceTimer: number | null = null
  let newestRequestId = 0
  let activeIndex = -1
  let currentPredictions: GooglePlacePrediction[] = []
  let selectingPlace = false

  const updateSuggestionPosition = () => {
    if (!suggestionsContainer.hidden) positionSuggestions(suggestionsContainer, addressInput)
  }

  const setActiveIndex = (nextIndex: number) => {
    const options = [...suggestionsContainer.querySelectorAll<HTMLButtonElement>('[role="option"]')]
    options.forEach((option, index) => {
      const active = index === nextIndex
      option.classList.toggle('is-active', active)
      option.setAttribute('aria-selected', active ? 'true' : 'false')
    })
    activeIndex = nextIndex
    const active = options[nextIndex]
    if (active) {
      addressInput.setAttribute('aria-activedescendant', active.id)
      active.scrollIntoView({ block: 'nearest' })
    } else {
      addressInput.removeAttribute('aria-activedescendant')
    }
  }

  const selectPrediction = async (prediction: GooglePlacePrediction) => {
    selectingPlace = true
    hideSuggestions(suggestionsContainer, addressInput)
    currentPredictions = []
    activeIndex = -1

    try {
      const place = prediction.toPlace()
      await place.fetchFields({
        fields: ['id', 'formattedAddress', 'addressComponents', 'location'],
      })

      const location = place.location
      const formattedAddress = place.formattedAddress?.trim() ?? ''
      const placeId = place.id?.trim() ?? ''
      const components = place.addressComponents ?? []
      const countryCode = componentShortValue(components, 'country').toUpperCase()

      if (!placeId || !formattedAddress || !location || countryCode !== 'NZ') {
        clearSelectedCheckoutPlace()
        return
      }

      const suburb = componentValue(components, 'sublocality_level_1', 'locality', 'postal_town')
      const postcode = componentValue(components, 'postal_code')

      selectedPlace = {
        placeId,
        formattedAddress,
        suburb,
        postcode,
        latitude: location.lat(),
        longitude: location.lng(),
      }

      setControlledInputValue(addressInput, selectedPlace.formattedAddress)
      if (suburbInput) setControlledInputValue(suburbInput, suburb)
      if (postcodeInput) setControlledInputValue(postcodeInput, postcode)
    } catch {
      clearSelectedCheckoutPlace()
    } finally {
      sessionToken = new places.AutocompleteSessionToken()
      selectingPlace = false
    }
  }

  const renderSuggestions = (predictions: GooglePlacePrediction[]) => {
    suggestionsContainer.replaceChildren()
    currentPredictions = predictions
    activeIndex = -1

    predictions.forEach((prediction, index) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.id = `${SUGGESTIONS_ID}-option-${index}`
      button.className = 'checkout-v3-address-suggestions__option'
      button.setAttribute('role', 'option')
      button.setAttribute('aria-selected', 'false')
      button.textContent = prediction.text.toString()
      button.addEventListener('mousedown', (event) => event.preventDefault())
      button.addEventListener('click', () => {
        void selectPrediction(prediction)
      })
      suggestionsContainer.appendChild(button)
    })

    if (predictions.length > 0) {
      const attribution = document.createElement('div')
      attribution.className = 'checkout-v3-address-suggestions__attribution'
      attribution.setAttribute('translate', 'no')
      attribution.textContent = 'Google Maps'
      suggestionsContainer.appendChild(attribution)
      positionSuggestions(suggestionsContainer, addressInput)
      suggestionsContainer.hidden = false
      addressInput.setAttribute('aria-expanded', 'true')
    } else {
      hideSuggestions(suggestionsContainer, addressInput)
    }
  }

  const requestSuggestions = async () => {
    const input = addressInput.value.trim()
    if (input.length < MIN_AUTOCOMPLETE_CHARACTERS) {
      hideSuggestions(suggestionsContainer, addressInput)
      currentPredictions = []
      return
    }

    const requestId = ++newestRequestId

    try {
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: [businessData.delivery.addressAutocomplete.country],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
        language: 'en-NZ',
        region: 'nz',
        sessionToken,
      })

      if (requestId !== newestRequestId) return

      const predictions = suggestions
        .map((suggestion) => suggestion.placePrediction)
        .filter((prediction): prediction is GooglePlacePrediction => Boolean(prediction))
        .slice(0, 5)

      renderSuggestions(predictions)
    } catch {
      if (requestId !== newestRequestId) return
      hideSuggestions(suggestionsContainer, addressInput)
      currentPredictions = []
    }
  }

  const handleInput = () => {
    if (selectingPlace) return

    if (selectedPlace && addressInput.value.trim() !== selectedPlace.formattedAddress.trim()) {
      clearSelectedCheckoutPlace()
      if (suburbInput?.value) setControlledInputValue(suburbInput, '')
      if (postcodeInput?.value) setControlledInputValue(postcodeInput, '')
    }

    if (debounceTimer !== null) window.clearTimeout(debounceTimer)
    debounceTimer = window.setTimeout(() => {
      void requestSuggestions()
    }, AUTOCOMPLETE_DEBOUNCE_MS)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (suggestionsContainer.hidden || currentPredictions.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((activeIndex + 1) % currentPredictions.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(activeIndex <= 0 ? currentPredictions.length - 1 : activeIndex - 1)
      return
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      void selectPrediction(currentPredictions[activeIndex])
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      hideSuggestions(suggestionsContainer, addressInput)
      currentPredictions = []
      activeIndex = -1
    }
  }

  const handleDocumentPointerDown = (event: PointerEvent) => {
    const target = event.target
    if (!(target instanceof Node)) return
    if (target === addressInput || suggestionsContainer.contains(target)) return
    hideSuggestions(suggestionsContainer, addressInput)
    currentPredictions = []
    activeIndex = -1
  }

  const handleFocus = () => {
    if (currentPredictions.length > 0) {
      positionSuggestions(suggestionsContainer, addressInput)
      suggestionsContainer.hidden = false
      addressInput.setAttribute('aria-expanded', 'true')
    }
  }

  addressInput.addEventListener('input', handleInput)
  addressInput.addEventListener('keydown', handleKeyDown)
  addressInput.addEventListener('focus', handleFocus)
  document.addEventListener('pointerdown', handleDocumentPointerDown, true)
  window.addEventListener('resize', updateSuggestionPosition)
  window.addEventListener('scroll', updateSuggestionPosition, true)

  return () => {
    if (debounceTimer !== null) window.clearTimeout(debounceTimer)
    newestRequestId += 1
    addressInput.removeEventListener('input', handleInput)
    addressInput.removeEventListener('keydown', handleKeyDown)
    addressInput.removeEventListener('focus', handleFocus)
    document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
    window.removeEventListener('resize', updateSuggestionPosition)
    window.removeEventListener('scroll', updateSuggestionPosition, true)
    addressInput.removeAttribute('aria-autocomplete')
    addressInput.removeAttribute('aria-controls')
    addressInput.removeAttribute('aria-expanded')
    addressInput.removeAttribute('aria-activedescendant')
    suggestionsContainer.remove()
  }
}
